import { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Alert, Stack, Chip, Divider, IconButton, Tooltip,
} from '@mui/material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import PageHeader from '../../components/common/PageHeader';
import { fetchCurrentUser, enrollMfa, confirmMfa, changeOwnPassword, logout } from '../../services/api/authService';
import { tokens } from '../../theme/theme';
import { useNavigate } from 'react-router-dom';

export default function AccountSettingsPage() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const [enrollment, setEnrollment] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState(null);
  const [mfaBusy, setMfaBusy] = useState(false);
  const [mfaDone, setMfaDone] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwError, setPwError] = useState(null);
  const [pwBusy, setPwBusy] = useState(false);

  useEffect(() => {
    fetchCurrentUser().then(setMe).catch((err) => setLoadError(err?.response?.data?.message || 'Could not load your account details.'));
  }, []);

  const handleStartEnroll = async () => {
    setMfaError(null);
    try {
      const data = await enrollMfa();
      setEnrollment(data);
    } catch (err) {
      setMfaError(err?.response?.data?.message || 'Could not start MFA enrollment.');
    }
  };

  const handleConfirmMfa = async () => {
    if (!/^\d{6}$/.test(mfaCode)) return setMfaError('Enter the 6-digit code from your authenticator app.');
    setMfaBusy(true);
    setMfaError(null);
    try {
      await confirmMfa(mfaCode);
      setMfaDone(true);
      setEnrollment(null);
      setMe((prev) => (prev ? { ...prev, mfaEnabled: true } : prev));
    } catch (err) {
      setMfaError(err?.response?.data?.message || 'That code did not match — check it and try again.');
    } finally {
      setMfaBusy(false);
    }
  };

  const handleChangePassword = async () => {
    setPwError(null);
    if (newPassword.length < 14) return setPwError('New password must be at least 14 characters (platform policy).');
    if (newPassword !== confirmPassword) return setPwError('New password and confirmation do not match.');
    setPwBusy(true);
    try {
      await changeOwnPassword(currentPassword, newPassword);
      // Changing your own password revokes every active session, including
      // this one — the sign-out is expected, not a bug.
      await logout();
      navigate('/login', { replace: true, state: { passwordChanged: true } });
    } catch (err) {
      setPwError(err?.response?.data?.message || 'Could not change your password — check your current password and try again.');
      setPwBusy(false);
    }
  };

  const copy = (text) => navigator.clipboard?.writeText(text).catch(() => {});

  return (
    <Box>
      <PageHeader title="Account Settings" subtitle="Manage your own login security — multi-factor authentication and password" />
      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}

      {me && (
        <Paper sx={{ p: 2.4, mb: 2.5 }}>
          <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Signed in as</Typography>
          <Typography sx={{ fontSize: '1rem', color: tokens.text.primary, fontWeight: 600, mt: 0.5 }}>{me.fullName}</Typography>
          <Typography sx={{ fontSize: '0.82rem', color: tokens.text.secondary }}>{me.email}</Typography>
          <Stack direction="row" spacing={0.75} sx={{ mt: 1 }}>
            {(me.roles || []).map((r) => (
              <Chip key={r} label={r.replaceAll('_', ' ')} size="small" sx={{ color: tokens.brand.main, border: `1px solid ${tokens.brand.main}`, backgroundColor: 'transparent' }} />
            ))}
          </Stack>
        </Paper>
      )}

      <Paper sx={{ p: 2.4, mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <VerifiedUserRoundedIcon sx={{ color: tokens.brand.main }} fontSize="small" />
          <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Multi-Factor Authentication</Typography>
          {me && (
            <Chip
              label={me.mfaEnabled ? 'Enabled' : 'Not enabled'} size="small"
              sx={{ ml: 'auto', color: me.mfaEnabled ? tokens.severity.low : tokens.severity.medium, border: `1px solid ${me.mfaEnabled ? tokens.severity.low : tokens.severity.medium}`, backgroundColor: 'transparent' }}
            />
          )}
        </Box>

        {mfaError && <Alert severity="error" sx={{ mb: 2 }}>{mfaError}</Alert>}
        {mfaDone && <Alert severity="success" sx={{ mb: 2 }}>MFA is now enabled on your account.</Alert>}

        {me?.mfaEnabled && !mfaDone ? (
          <Typography sx={{ fontSize: '0.84rem', color: tokens.text.secondary }}>
            MFA is already enabled on your account. Contact an administrator if you need to reset your authenticator device.
          </Typography>
        ) : !enrollment ? (
          <Button variant="contained" onClick={handleStartEnroll} disabled={mfaDone}>Set Up MFA</Button>
        ) : (
          <Stack spacing={2}>
            <Typography sx={{ fontSize: '0.84rem', color: tokens.text.secondary }}>
              Scan this into your authenticator app, or enter the secret manually if scanning isn't available:
            </Typography>
            <Box sx={{ p: 1.5, borderRadius: 1.5, border: `1px solid ${tokens.border}`, fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', wordBreak: 'break-all' }}>
              {enrollment.secret}
              <Tooltip title="Copy secret"><IconButton size="small" onClick={() => copy(enrollment.secret)}><ContentCopyRoundedIcon fontSize="small" /></IconButton></Tooltip>
            </Box>
            <Box sx={{ p: 1.5, borderRadius: 1.5, border: `1px solid ${tokens.border}`, fontFamily: '"JetBrains Mono", monospace', fontSize: '0.72rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', wordBreak: 'break-all', color: tokens.text.muted }}>
              {enrollment.otpAuthUri}
              <Tooltip title="Copy setup URI"><IconButton size="small" onClick={() => copy(enrollment.otpAuthUri)}><ContentCopyRoundedIcon fontSize="small" /></IconButton></Tooltip>
            </Box>
            <Divider />
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <TextField
                label="6-digit code" size="small" value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputProps={{ inputMode: 'numeric', maxLength: 6 }}
              />
              <Button variant="contained" onClick={handleConfirmMfa} disabled={mfaBusy}>{mfaBusy ? 'Confirming…' : 'Confirm & Enable'}</Button>
            </Stack>
          </Stack>
        )}
      </Paper>

      <Paper sx={{ p: 2.4 }}>
        <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Change Password</Typography>
        <Typography sx={{ fontSize: '0.78rem', color: tokens.text.muted, mb: 2 }}>
          Changing your password signs you out of every active session, including this one.
        </Typography>
        {pwError && <Alert severity="error" sx={{ mb: 2 }}>{pwError}</Alert>}
        <Stack spacing={2} sx={{ maxWidth: 380 }}>
          <TextField label="Current password" type="password" size="small" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} autoComplete="current-password" />
          <TextField label="New password" type="password" size="small" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} autoComplete="new-password" helperText="At least 14 characters." />
          <TextField label="Confirm new password" type="password" size="small" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} autoComplete="new-password" />
          <Button variant="contained" onClick={handleChangePassword} disabled={pwBusy || !currentPassword || !newPassword}>
            {pwBusy ? 'Changing…' : 'Change Password'}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
